/**
 * RevertReleaseModal Component
 * 
 * Modal for confirming revert to a previous release
 * Shows what will be restored
 */

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button } from './ui'
import { releaseCounts } from '../utils/releaseCounts'

export default function RevertReleaseModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  release,
  activeRelease
}) {
  const [isReverting, setIsReverting] = useState(false)

  const handleConfirm = async () => {
    try {
      setIsReverting(true)
      await onConfirm()
      onClose()
    } catch (error) {
      console.error('[REVERT MODAL] Error:', error)
    } finally {
      setIsReverting(false)
    }
  }

  if (!release) return null

  // A release that snapshotted the Manifold catalog will restore pricing too,
  // and pricing does not restore the way the other resources do.
  const includesPricing = releaseCounts(release).some(({ key }) =>
    ['item_families', 'items', 'item_prices', 'coupons'].includes(key)
  )

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Revert to Previous Release?</DialogTitle>
          <DialogDescription>
            This will restore your configuration to a previous state and create a new release.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Warning Banner */}
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">
                  Important: This will overwrite current data
                </h4>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  All current drug programs, pharmacies, and physicians will be replaced with data from
                  the selected release. A new release will be created to track this change.
                </p>
                {includesPricing && (
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                    Pricing is restored differently: prices and coupons move back to their
                    values in this release, and anything created since is archived rather
                    than deleted — a price a live subscription bills against is never
                    removed. Changes take effect at checkout immediately.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Current Release Info */}
          {activeRelease && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span>Current Release</span>
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 rounded-full">
                  ACTIVE
                </span>
              </h4>
              <div className="text-sm space-y-1">
                <div>
                  <span className="font-medium">Release #{activeRelease.release_number}:</span> {activeRelease.release_name}
                </div>
                <div className="text-muted-foreground">
                  Published: {formatDate(activeRelease.published_at)}
                </div>
                <div className="flex flex-wrap gap-4 text-xs mt-2">
                  {releaseCounts(activeRelease).map(({ key, label, count }) => (
                    <span key={key}>{count} {label}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Arrow */}
          <div className="text-center text-2xl text-muted-foreground">
            ↓
          </div>

          {/* Target Release Info */}
          <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <span>Restore to This Release</span>
              {release.revert_of && (
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100 rounded-full">
                  REVERT
                </span>
              )}
            </h4>
            <div className="text-sm space-y-1">
              <div>
                <span className="font-medium">Release #{release.release_number}:</span> {release.release_name}
              </div>
              <div className="text-muted-foreground">
                By: {release.created_by?.name || 'Unknown'} ({release.created_by?.email})
              </div>
              <div className="text-muted-foreground">
                Published: {formatDate(release.published_at)}
              </div>
              {release.description && (
                <div className="text-muted-foreground">
                  {release.description}
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-xs mt-2">
                {releaseCounts(release).map(({ key, label, count }) => (
                  <span key={key}>{count} {label}</span>
                ))}
              </div>
            </div>
          </div>

          {/* What will happen */}
          <div className="space-y-2">
            <h4 className="font-semibold">What will happen:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li>Current data will be backed up in a new release snapshot</li>
              <li>Data from Release #{release.release_number} will be restored</li>
              {includesPricing && (
                <li>Pricing is replayed forward through Manifold — nothing is deleted</li>
              )}
              <li>A new "Revert" release will be created and marked as active</li>
              <li>All documents will be updated with your user ID</li>
            </ol>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isReverting}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={handleConfirm}
            disabled={isReverting}
          >
            {isReverting ? 'Reverting...' : `Yes, Revert to Release #${release.release_number}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

